import { 
  CognitoIdentityProviderClient, 
  ConfirmSignUpCommand, 
  ResendConfirmationCodeCommand, 
  SignUpCommand,
  InitiateAuthCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  GetUserCommand,
  GlobalSignOutCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { COGNITO } from '../../constants/api.constants';
import { persistStore } from '../../persist-store';

// Initialize the persist store for secure storage
persistStore.initSecureWorker();

// Enable logging in development environment
if (process.env.NODE_ENV === 'development') {
  persistStore.configure({ enableLogging: true });
}

// Auth token key in secure storage
const AUTH_TOKEN_KEY = 'auth_tokens';

// Initialize Cognito client
const cognitoClient = new CognitoIdentityProviderClient({ 
  region: COGNITO.REGION 
});

interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  userId: string;
}

interface CognitoUserData {
  email: string;
  email_verified: boolean;
  sub: string;
  name?: string;
}

interface StoredAuthTokens extends AuthTokens {
  expiresAt: number;
}

const TOKEN_REFRESH_BUFFER_MS = 60_000;
let inFlightRefreshPromise: Promise<boolean> | null = null;

const getStoredAuthTokens = async (): Promise<StoredAuthTokens | null> => (
  persistStore.getSecure<StoredAuthTokens>(AUTH_TOKEN_KEY)
);

const hasFreshIdToken = (tokens: Pick<StoredAuthTokens, 'idToken' | 'expiresAt'> | null): boolean => (
  Boolean(
    tokens?.idToken &&
    tokens.expiresAt &&
    Date.now() < tokens.expiresAt - TOKEN_REFRESH_BUFFER_MS
  )
);

/**
 * Register a new user
 */
export const register = async (
  email: string,
  password: string
): Promise<boolean> => {
  try {
    const command = new SignUpCommand({
      ClientId: COGNITO.CLIENT_ID,
      Username: email,
      Password: password
    });
    
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error('Registration error:', error);
    return false;
  }
};

/**
 * Login with email and password
 */
export const login = async (
  email: string,
  password: string,
  rememberMe: boolean = false
): Promise<AuthTokens | undefined> => {
  try {
    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: COGNITO.CLIENT_ID,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password
      }
    });
    
    const response = await cognitoClient.send(command);
    const authResult = response.AuthenticationResult;
    
    if (authResult?.AccessToken && authResult?.IdToken) {
      // Extract user ID from JWT token using browser-compatible base64 decoding
      const base64Url = authResult.IdToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      const userId = payload.sub;
      
      // Store tokens securely
      const expiresIn = authResult.ExpiresIn || 3600;
      const expiresAt = new Date().getTime() + expiresIn * 1000;
      
      const tokens: AuthTokens = {
        accessToken: authResult.AccessToken,
        idToken: authResult.IdToken,
        refreshToken: authResult.RefreshToken || '',
        userId,
        expiresIn
      };
      
      // Store tokens in secure storage
      await persistStore.putSecure(AUTH_TOKEN_KEY, {
        ...tokens,
        expiresAt
      });
      
      // Store refresh token based on rememberMe preference
      if (authResult.RefreshToken) {
        if (rememberMe) {
          // If rememberMe is true, store in cookie for persistence across sessions
          await persistStore.putCookie('refresh_token', authResult.RefreshToken, {
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'strict',
            maxAge: 30 * 24 * 60 * 60 // 30 days
          });
        } else {
          // If rememberMe is false, store in session storage (cleared when browser closes)
          await persistStore.putSession('refresh_token', authResult.RefreshToken);
        }
      }
      
      // Store userId in localStorage for convenience (non-sensitive)
      await persistStore.putLocal('userId', userId);
      
      return tokens;
    }
    return undefined;
  } catch (error) {
    console.error('Login error:', error);
    return undefined;
  }
};

/**
 * Log out the current user
 */
export const logout = async (): Promise<void> => {
  try {
    // Get the access token to sign out from Cognito
    const tokens = await persistStore.getSecure<{
      accessToken: string;
    }>(AUTH_TOKEN_KEY);
    
    if (tokens?.accessToken) {
      // Sign out from Cognito
      const command = new GlobalSignOutCommand({
        AccessToken: tokens.accessToken
      });
      
      await cognitoClient.send(command);
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear secure tokens
    await persistStore.removeSecure(AUTH_TOKEN_KEY);
    
    // Clear refresh token from all storage locations
    await persistStore.removeCookie('refresh_token', { path: '/' });
    await persistStore.removeSession('refresh_token');
    
    // Clear any auth-related data from localStorage
    await persistStore.removeLocal('userId');
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  // First check if we have valid tokens in secure storage
  const tokens = await getStoredAuthTokens();
  
  const hasValidTokens = hasFreshIdToken(tokens);
  
  // If tokens are valid, return true
  if (hasValidTokens) {
    return true;
  }
  
  // If tokens are expired or missing, try to refresh using the refresh token
  // Check all possible storage locations
  const cookieRefreshToken = await persistStore.getCookie<string>('refresh_token');
  const sessionRefreshToken = await persistStore.getSession<string>('refresh_token');
  const secureTokens = await persistStore.getSecure<{
    refreshToken: string;
  }>(AUTH_TOKEN_KEY);
  
  if (cookieRefreshToken || sessionRefreshToken || secureTokens?.refreshToken) {
    try {
      const refreshed = await refreshToken();
      return refreshed;
    } catch (error) {
      console.error('Error refreshing token during authentication check:', error);
      return false;
    }
  }
  
  return false;
};

/**
 * Get current JWT token
 */
export const getToken = async (): Promise<string | null> => {
  const tokens = await persistStore.getSecure<{
    idToken: string;
  }>(AUTH_TOKEN_KEY);
  
  return tokens?.idToken || null;
};

/**
 * Get authentication headers for API requests
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  let tokens = await getStoredAuthTokens();

  if (tokens?.idToken && !hasFreshIdToken(tokens)) {
    const refreshed = await refreshToken();

    if (!refreshed) {
      return {};
    }

    tokens = await getStoredAuthTokens();
  }

  if (!tokens?.idToken) {
    return {};
  }
  
  return {
    'Authorization': `Bearer ${tokens.idToken}`,
    'userId': tokens.userId || ''
  };
};

/**
 * Get user ID
 */
export const getUserId = async (): Promise<string | null> => {
  // Try to get from secure storage first
  const tokens = await persistStore.getSecure<{
    userId: string;
  }>(AUTH_TOKEN_KEY);
  
  if (tokens?.userId) {
    return tokens.userId;
  }
  
  // Fall back to localStorage
  return await persistStore.getLocal<string>('userId') || null;
};

/**
 * Refresh the JWT token
 */
export const refreshToken = async (): Promise<boolean> => {
  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  inFlightRefreshPromise = (async (): Promise<boolean> => {
    // Try to get refresh token from cookie or session storage first
    let refreshTokenValue = await persistStore.getCookie<string>('refresh_token');
    
    // If not in cookie, try session storage
    if (!refreshTokenValue) {
      refreshTokenValue = await persistStore.getSession<string>('refresh_token');
    }
    
    // If not in cookie or session storage, fall back to secure storage
    if (!refreshTokenValue) {
      const tokens = await persistStore.getSecure<{
        refreshToken: string;
      }>(AUTH_TOKEN_KEY);
      
      if (!tokens?.refreshToken) {
        return false;
      }
      
      refreshTokenValue = tokens.refreshToken;
    }
    
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: COGNITO.CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: refreshTokenValue
        }
      });
      
      const response = await cognitoClient.send(command);
      const authResult = response.AuthenticationResult;
      
      if (authResult?.AccessToken && authResult?.IdToken) {
        // Get existing tokens
        const existingTokens = await persistStore.getSecure<Record<string, unknown>>(AUTH_TOKEN_KEY) || {};
        
        // Update tokens
        const expiresAt = new Date().getTime() + (authResult.ExpiresIn || 3600) * 1000;
        const nextTokens: Record<string, unknown> = {
          ...existingTokens,
          accessToken: authResult.AccessToken,
          idToken: authResult.IdToken,
          expiresAt
        };
        
        // Update refresh token in secure storage
        if (authResult.RefreshToken) {
          // Check if we have a cookie or session storage token, and update accordingly
          const hasCookie = await persistStore.hasCookie('refresh_token');
          const hasSession = await persistStore.hasSession('refresh_token');
          nextTokens.refreshToken = authResult.RefreshToken;
          
          if (hasCookie) {
            // Update cookie if it exists
            await persistStore.putCookie('refresh_token', authResult.RefreshToken, {
              path: '/',
              secure: window.location.protocol === 'https:',
              sameSite: 'strict',
              maxAge: 30 * 24 * 60 * 60 // 30 days
            });
          } else if (hasSession) {
            // Update session storage if it exists
            await persistStore.putSession('refresh_token', authResult.RefreshToken);
          }
        }

        await persistStore.putSecure(AUTH_TOKEN_KEY, nextTokens);
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      await logout(); // Clear invalid tokens
      return false;
    }
  })();

  try {
    return await inFlightRefreshPromise;
  } finally {
    inFlightRefreshPromise = null;
  }
};

/**
 * Get user info from the token
 */
export const getUserInfo = async (): Promise<CognitoUserData | undefined> => {
  try {
    const tokens = await persistStore.getSecure<{
      accessToken: string;
    }>(AUTH_TOKEN_KEY);
    
    if (!tokens?.accessToken) {
      return undefined;
    }
    
    const command = new GetUserCommand({
      AccessToken: tokens.accessToken
    });
    
    const response = await cognitoClient.send(command);
    
    // Transform Cognito user attributes to our format
    const userData: CognitoUserData = {
      sub: '',
      email: '',
      email_verified: false
    };
    
    if (response.UserAttributes) {
      response.UserAttributes.forEach(attr => {
        if (attr.Name === 'sub') userData.sub = attr.Value || '';
        if (attr.Name === 'email') userData.email = attr.Value || '';
        if (attr.Name === 'email_verified') userData.email_verified = attr.Value === 'true';
        if (attr.Name === 'name') userData.name = attr.Value;
      });
    }
    
    return userData;
  } catch (error) {
    console.error('Get user info error:', error);
    return undefined;
  }
};

/**
 * Confirm user registration with code
 */
export const confirmRegistration = async (
  email: string,
  code: string
): Promise<boolean> => {
  try {
    const command = new ConfirmSignUpCommand({
      ClientId: COGNITO.CLIENT_ID,
      Username: email,
      ConfirmationCode: code
    });
    
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error('Confirm registration error:', error);
    return false;
  }
};

/**
 * Resend confirmation code to user's email
 */
export const resendConfirmationCode = async (
  email: string
): Promise<boolean> => {
  try {
    const command = new ResendConfirmationCodeCommand({
      ClientId: COGNITO.CLIENT_ID,
      Username: email
    });
    
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error('Resend confirmation code error:', error);
    return false;
  }
};

/**
 * Initiate forgot password flow
 */
export const forgotPassword = async (email: string): Promise<boolean> => {
  try {
    const command = new ForgotPasswordCommand({
      ClientId: COGNITO.CLIENT_ID,
      Username: email
    });
    
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error('Forgot password error:', error);
    return false;
  }
};

/**
 * Complete password reset
 */
export const resetPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<boolean> => {
  try {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: COGNITO.CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword
    });
    
    await cognitoClient.send(command);
    return true;
  } catch (error) {
    console.error('Reset password error:', error);
    return false;
  }
}
